import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import { formatCurrency, formatPercent, getStatusColor } from '@/lib/budget-utils';
import type { BudgetStatus } from '@/types/budget.types';

interface BudgetCardProps {
  title: string;
  primaryValue?: string | number;
  secondaryValue?: string | number;
  tertiaryValue?: string | number;
  progressPercent?: number;
  status?: BudgetStatus;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: React.ReactNode;
  tooltip?: string;
  variant?: 'default' | 'compact';
  // Aliases for backward compatibility
  value?: string | number;
  subtitle?: string | number;
}

export function BudgetCard({
  title,
  primaryValue,
  secondaryValue,
  tertiaryValue,
  progressPercent,
  status = 'green',
  trend,
  trendValue,
  icon,
  tooltip,
  variant = 'default',
  value,
  subtitle,
}: BudgetCardProps) {
  // Use aliases if primary values not provided
  const displayPrimaryValue = primaryValue ?? value ?? '';
  const displaySecondaryValue = secondaryValue ?? subtitle;
  const statusColor = getStatusColor(status);
  const bgColor = status === 'green' 
    ? 'bg-green-50 dark:bg-green-950/20' 
    : status === 'amber' 
    ? 'bg-amber-50 dark:bg-amber-950/20' 
    : status === 'red'
    ? 'bg-red-50 dark:bg-red-950/20'
    : 'bg-gray-50 dark:bg-gray-950/20';

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };

  const getTrendColor = () => {
    // For costs, up is bad, down is good
    // For revenue, up is good, down is bad
    // This is handled by the status prop
    if (trend === 'up') return 'text-green-600 dark:text-green-400';
    if (trend === 'down') return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  if (variant === 'compact') {
    return (
      <Card className={`${bgColor} border-l-4 ${statusColor.replace('text-', 'border-')}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              <p className="text-2xl font-bold mt-1">{displayPrimaryValue}</p>
              {displaySecondaryValue && (
                <p className="text-sm text-muted-foreground mt-1">{displaySecondaryValue}</p>
              )}
            </div>
            {icon && (
              <div className={`${statusColor} opacity-80`}>
                {icon}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${bgColor} border-l-4 ${statusColor?.replace('text-', 'border-') || 'border-gray-600'}`}>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-medium text-muted-foreground">
            {title}
          </CardTitle>
          {tooltip && (
            <div title={tooltip}>
              <AlertCircle className="h-3 w-3 text-muted-foreground cursor-help" />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 px-4 pb-3">
        {/* Primary Value */}
        <div className="flex items-baseline justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xl font-bold tracking-tight truncate">
              {typeof displayPrimaryValue === 'number' ? formatCurrency(displayPrimaryValue) : displayPrimaryValue}
            </p>
            {displaySecondaryValue && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {typeof displaySecondaryValue === 'number' ? formatCurrency(displaySecondaryValue) : displaySecondaryValue}
              </p>
            )}
          </div>
          {icon && (
            <div className={`${statusColor} opacity-70 ml-2 flex-shrink-0`}>
              {icon}
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {progressPercent !== undefined && (
          <div className="space-y-0.5">
            <Progress 
              value={progressPercent} 
              className="h-1.5"
              accent={
                status === 'green' 
                  ? 'success' 
                  : status === 'amber' 
                  ? 'warning' 
                  : 'destructive'
              }
            />
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{formatPercent(progressPercent)}</span>
              {tertiaryValue && <span className="truncate ml-1">{tertiaryValue}</span>}
            </div>
          </div>
        )}

        {/* Trend Indicator */}
        {trend && trendValue && (
          <div className="flex items-center gap-1 text-xs">
            <span className={getTrendColor()}>
              {getTrendIcon()}
            </span>
            <span className={getTrendColor()}>
              {trendValue}
            </span>
            <span className="text-muted-foreground text-[10px]">vs last month</span>
          </div>
        )}

        {/* Status Badge */}
        <div className="flex items-center justify-between">
          <Badge 
            variant={status === 'green' ? 'default' : status === 'amber' ? 'secondary' : 'destructive'}
            className="text-[10px] h-5 px-2"
          >
            {status === 'green' ? 'On Track' : status === 'amber' ? 'Warning' : 'Critical'}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
